import React from 'react';
import Icon from '../../../components/AppIcon';

const EmailStats = () => {
  const stats = [
    {
      label: 'Open Rate',
      value: '68.5%',
      change: '+12.3%',
      trend: 'up',
      icon: 'Mail',
      color: 'text-success',
      bgColor: 'bg-green-50',
      description: 'Above industry average of 21.33%'
    },
    {
      label: 'Click Rate',
      value: '24.7%',
      change: '+8.1%',
      trend: 'up',
      icon: 'MousePointer',
      color: 'text-primary',
      bgColor: 'bg-blue-50',
      description: 'Excellent engagement metrics'
    },
    {
      label: 'Conversion Rate',
      value: '15.2%',
      change: '+5.4%',
      trend: 'up',
      icon: 'Target',
      color: 'text-accent',
      bgColor: 'bg-red-50',
      description: 'Strong waitlist conversion'
    },
    {
      label: 'Unsubscribe Rate',
      value: '0.8%',
      change: '-0.3%',
      trend: 'down',
      icon: 'UserMinus',
      color: 'text-gold',
      bgColor: 'bg-yellow-50',
      description: 'Well below 2% benchmark'
    }
  ];

  const insights = [
    {
      title: 'Personalization Impact',
      description: 'Emails with first names see 26% higher open rates',
      icon: 'User',
      color: 'text-teal'
    },
    {
      title: 'Mobile Optimization',
      description: '73% of emails opened on mobile devices',
      icon: 'Smartphone',
      color: 'text-primary'
    },
    {
      title: 'Send Time Optimization',
      description: 'Tuesday 10 AM shows best engagement',
      icon: 'Clock',
      color: 'text-accent'
    },
    {
      title: 'Subject Line Performance',
      description: 'Family-focused subjects perform 31% better',
      icon: 'MessageSquare',
      color: 'text-gold'
    }
  ];

  return (
    <div className="bg-white rounded-xl shadow-soft border border-border p-6">
      <div className="mb-6">
        <h3 className="text-xl font-heading font-semibold text-foreground mb-2">
          Email Performance Analytics
        </h3>
        <p className="text-text-secondary">
          Real-time metrics from our email campaigns and industry benchmarks
        </p>
      </div>
      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats?.map((stat, index) => (
          <div key={index} className="p-4 rounded-lg border border-border bg-surface">
            <div className="flex items-center justify-between mb-3">
              <div className={`p-2 rounded-lg ${stat?.bgColor}`}>
                <Icon name={stat?.icon} size={18} className={stat?.color} />
              </div>
              <div className={`flex items-center text-xs font-medium ${
                stat?.trend === 'up' ? 'text-success' : 'text-gold'
              }`}>
                <Icon 
                  name={stat?.trend === 'up' ? 'TrendingUp' : 'TrendingDown'} 
                  size={12} 
                  className="mr-1" 
                />
                {stat?.change}
              </div>
            </div>
            
            <div className="mb-2">
              <div className="text-2xl font-heading font-bold text-foreground">
                {stat?.value}
              </div>
              <div className="text-sm font-medium text-text-secondary">
                {stat?.label}
              </div>
            </div>
            
            <div className="text-xs text-text-secondary">
              {stat?.description}
            </div>
          </div>
        ))}
      </div>
      {/* Performance Insights */}
      <div className="mb-6">
        <h4 className="font-heading font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Lightbulb" size={18} className="mr-2 text-gold" />
          Performance Insights
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {insights?.map((insight, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-surface border border-border">
              <Icon name={insight?.icon} size={16} className={`${insight?.color} mt-1 flex-shrink-0`} />
              <div>
                <h5 className="font-medium text-foreground text-sm mb-1">
                  {insight?.title}
                </h5>
                <p className="text-xs text-text-secondary">
                  {insight?.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Email Client Compatibility */}
      <div className="mb-6">
        <h4 className="font-heading font-semibold text-foreground mb-4 flex items-center">
          <Icon name="Monitor" size={18} className="mr-2 text-secondary" />
          Email Client Compatibility
        </h4>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { name: 'Gmail', support: '100%', icon: 'Mail' },
            { name: 'Outlook', support: '98%', icon: 'Monitor' },
            { name: 'Apple Mail', support: '100%', icon: 'Smartphone' },
            { name: 'Yahoo', support: '95%', icon: 'Globe' }
          ]?.map((client, index) => (
            <div key={index} className="text-center p-3 rounded-lg bg-surface border border-border">
              <Icon name={client?.icon} size={20} className="text-primary mx-auto mb-2" />
              <div className="text-sm font-medium text-foreground">{client?.name}</div>
              <div className="text-xs text-success font-semibold">{client?.support}</div>
            </div>
          ))}
        </div>
      </div>
      {/* Best Practices */}
      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h5 className="font-medium text-blue-900 mb-3 flex items-center">
          <Icon name="CheckCircle" size={16} className="mr-2" />
          Email Best Practices Applied
        </h5>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-800">
          <div className="flex items-center">
            <Icon name="Check" size={12} className="mr-2 text-blue-600" />
            <span>Responsive design for all devices</span>
          </div>
          <div className="flex items-center">
            <Icon name="Check" size={12} className="mr-2 text-blue-600" />
            <span>Dark mode compatibility</span>
          </div>
          <div className="flex items-center">
            <Icon name="Check" size={12} className="mr-2 text-blue-600" />
            <span>Alt text for all images</span>
          </div>
          <div className="flex items-center">
            <Icon name="Check" size={12} className="mr-2 text-blue-600" />
            <span>Clear unsubscribe options</span>
          </div>
          <div className="flex items-center">
            <Icon name="Check" size={12} className="mr-2 text-blue-600" />
            <span>Optimized for screen readers</span>
          </div>
          <div className="flex items-center">
            <Icon name="Check" size={12} className="mr-2 text-blue-600" />
            <span>CAN-SPAM compliant</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailStats;